// roleUtils.js
import { ROLE_HIERARCHY, ROLES } from "./roles";

/**
 * Check if the user's role is equal to or higher than the required role.
 * @param {string} userRole - The current user's role.
 * @param {string} requiredRole - The minimum role required.
 * @returns {boolean}
 */
export const hasMinimumRole = (userRoleStr, requiredRole) => {
    if (!userRoleStr) return false;
    // Convert the comma-separated roles into an array (all lowercased)
    const roles = userRoleStr.split(",").map(r => r.trim().toLowerCase());
    const required = requiredRole.toLowerCase();
    
    // Compute the highest privilege level from the user's roles.
    const highestLevel = roles.reduce((max, role) => {
      const level = ROLE_HIERARCHY[role] !== undefined ? ROLE_HIERARCHY[role] : -Infinity;
      return level > max ? level : max;
    }, -Infinity);
    
    return highestLevel >= ROLE_HIERARCHY[required];
  };
  

/**
 * Filter data based on the current user's team.
 * - If the user is a TEAM_LEAD, they should see records where:
 *    - assigned_to is equal to their own id, or
 *    - assigned_to is one of the users whose team field equals the team lead's id.
 * - If the user is a regular USER, they see only records assigned to them.
 * - If the user is ADMIN, no filtering is applied.
 *
 * @param {Array} data - Array of records.
 * @param {object} currentUser - The current logged-in user.
 * @param {Array} users - Array of all user records.
 * @returns {Array}
 */
export const filterDataForUser = (data, currentUser, users = []) => {
  
    if (!currentUser) {
      return data;
    }
  
    // If the user is a TEAM_LEAD or higher (ADMIN), apply team lead filtering.
    if (hasMinimumRole(currentUser.role, ROLES.TEAM_LEAD)) {
      // Identify team members: users whose team equals the current user's id.
      const teamMemberIds = users
        .filter(u => String(u.team) === String(currentUser.id))
        .map(u => u.id);
  
      const filtered = data.filter(record => {
        const assignedTo = record.assigned_to;
        const isOwnRecord = assignedTo === currentUser.id;
        const isTeamMemberRecord = teamMemberIds.includes(assignedTo);

        return isOwnRecord || isTeamMemberRecord;
      });
      return filtered;
    }
  
    // For regular USER, only show records assigned to them.
    if (currentUser.role === ROLES.USER) {
      const filtered = data.filter(record => {
        const match = record.assigned_to === currentUser.id;
        return match;
      });
      return filtered;
    }
  
    return data;
  };
  